import { Device, AdminUser, AuditLog } from '../types';

export interface ExportOptions {
  format: 'csv' | 'excel' | 'pdf';
  fields: string[];
  filters?: any;
  filename?: string;
}

export class ExportService {
  // Export devices to CSV
  static exportDevicesToCSV(devices: Device[], options: ExportOptions): void {
    const { fields, filename = 'devices' } = options;
    
    // Define all possible fields
    const allFields = {
      deviceId: 'Device ID',
      platform: 'Platform',
      deviceModel: 'Device Model',
      deviceBrand: 'Device Brand',
      osVersion: 'OS Version',
      appVersion: 'App Version',
      country: 'Country',
      countryCode: 'Country Code',
      isBlocked: 'Is Blocked',
      suspiciousActivity: 'Suspicious Activity',
      totalFaceSwaps: 'Total Face Swaps',
      lastActive: 'Last Active',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    };

    // Filter fields based on selection
    const selectedFields = fields.length > 0 ? fields : Object.keys(allFields);
    const headers = selectedFields.map(field => allFields[field as keyof typeof allFields]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...devices.map(device => 
        selectedFields.map(field => {
          const value = device[field as keyof Device];
          // Handle different data types
          if (value === null || value === undefined) return '';
          if (typeof value === 'boolean') return value ? 'Yes' : 'No';
          if (typeof value === 'object') return JSON.stringify(value);
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv');
  }

  // Export users to CSV
  static exportUsersToCSV(users: AdminUser[], options: ExportOptions): void {
    const { fields, filename = 'users' } = options;
    
    const allFields = {
      name: 'Name',
      email: 'Email',
      role: 'Role',
      isActive: 'Is Active',
      lastLogin: 'Last Login',
      createdAt: 'Created At',
      updatedAt: 'Updated At',
    };

    const selectedFields = fields.length > 0 ? fields : Object.keys(allFields);
    const headers = selectedFields.map(field => allFields[field as keyof typeof allFields]);

    const csvContent = [
      headers.join(','),
      ...users.map(user => 
        selectedFields.map(field => {
          const value = user[field as keyof AdminUser];
          if (value === null || value === undefined) return '';
          if (typeof value === 'boolean') return value ? 'Yes' : 'No';
          if (typeof value === 'object') return JSON.stringify(value);
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv');
  }

  // Export audit logs to CSV
  static exportAuditLogsToCSV(logs: AuditLog[], options: ExportOptions): void {
    const { fields, filename = 'audit_logs' } = options;
    
    const allFields = {
      adminName: 'Admin Name',
      action: 'Action',
      resource: 'Resource',
      resourceId: 'Resource ID',
      details: 'Details',
      ipAddress: 'IP Address',
      userAgent: 'User Agent',
      timestamp: 'Timestamp',
    };

    const selectedFields = fields.length > 0 ? fields : Object.keys(allFields);
    const headers = selectedFields.map(field => allFields[field as keyof typeof allFields]);

    const csvContent = [
      headers.join(','),
      ...logs.map(log => 
        selectedFields.map(field => {
          const value = log[field as keyof AuditLog];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value);
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    this.downloadFile(csvContent, `${filename}.csv`, 'text/csv');
  }

  // Export to Excel (using a simple approach with CSV)
  static exportToExcel(data: any[], options: ExportOptions): void {
    // For now, we'll use CSV format as Excel
    // In a real implementation, you'd use a library like xlsx
    this.exportDevicesToCSV(data, { ...options, format: 'csv' });
  }

  // Export to PDF (placeholder - would need a PDF library)
  static exportToPDF(data: any[], options: ExportOptions): void {
    // This would require a PDF generation library like jsPDF
    console.log('PDF export not implemented yet');
    alert('PDF export feature coming soon!');
  }

  // Generic export method
  static export(data: any[], type: 'devices' | 'users' | 'audit_logs', options: ExportOptions): void {
    switch (options.format) {
      case 'csv':
        if (type === 'devices') {
          this.exportDevicesToCSV(data, options);
        } else if (type === 'users') {
          this.exportUsersToCSV(data, options);
        } else if (type === 'audit_logs') {
          this.exportAuditLogsToCSV(data, options);
        }
        break;
      case 'excel':
        this.exportToExcel(data, options);
        break;
      case 'pdf':
        this.exportToPDF(data, options);
        break;
      default:
        console.error('Unsupported export format');
    }
  }

  // Helper method to download file
  private static downloadFile(content: string, filename: string, mimeType: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // Get available fields for each data type
  static getAvailableFields(type: 'devices' | 'users' | 'audit_logs'): { value: string; label: string }[] {
    switch (type) {
      case 'devices':
        return [
          { value: 'deviceId', label: 'Device ID' },
          { value: 'platform', label: 'Platform' },
          { value: 'deviceModel', label: 'Device Model' },
          { value: 'deviceBrand', label: 'Device Brand' },
          { value: 'osVersion', label: 'OS Version' },
          { value: 'appVersion', label: 'App Version' },
          { value: 'country', label: 'Country' },
          { value: 'countryCode', label: 'Country Code' },
          { value: 'isBlocked', label: 'Is Blocked' },
          { value: 'suspiciousActivity', label: 'Suspicious Activity' },
          { value: 'totalFaceSwaps', label: 'Total Face Swaps' },
          { value: 'lastActive', label: 'Last Active' },
          { value: 'createdAt', label: 'Created At' },
          { value: 'updatedAt', label: 'Updated At' },
        ];
      case 'users':
        return [
          { value: 'name', label: 'Name' },
          { value: 'email', label: 'Email' },
          { value: 'role', label: 'Role' },
          { value: 'isActive', label: 'Is Active' },
          { value: 'lastLogin', label: 'Last Login' },
          { value: 'createdAt', label: 'Created At' },
          { value: 'updatedAt', label: 'Updated At' },
        ];
      case 'audit_logs':
        return [
          { value: 'adminName', label: 'Admin Name' },
          { value: 'action', label: 'Action' },
          { value: 'resource', label: 'Resource' },
          { value: 'resourceId', label: 'Resource ID' },
          { value: 'details', label: 'Details' },
          { value: 'ipAddress', label: 'IP Address' },
          { value: 'userAgent', label: 'User Agent' },
          { value: 'timestamp', label: 'Timestamp' },
        ];
      default:
        return [];
    }
  }
}

export default ExportService;
